import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Users, Loader2 } from 'lucide-react';

interface Profile {
  id: string;
  name: string;
  profile_image?: string;
}

const CreateGroupChat = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchContacts();
  }, [user, navigate]);

  const fetchContacts = async () => {
    if (!user) return;

    try {
      // Get all chat participants for chats the user is in
      const { data: userChats, error: chatsError } = await supabase
        .from('chat_participants')
        .select('chat_id')
        .eq('user_id', user.id);

      if (chatsError) throw chatsError;

      if (!userChats || userChats.length === 0) {
        setProfiles([]);
        return;
      }

      const chatIds = userChats.map(c => c.chat_id);

      // Get all other participants in those chats
      const { data: otherParticipants, error: participantsError } = await supabase
        .from('chat_participants')
        .select('user_id')
        .in('chat_id', chatIds)
        .neq('user_id', user.id);

      if (participantsError) throw participantsError;

      if (!otherParticipants || otherParticipants.length === 0) {
        setProfiles([]);
        return;
      }

      // Get unique user IDs
      const uniqueUserIds = [...new Set(otherParticipants.map(p => p.user_id))];

      // Fetch profiles using the secure RPC function
      const profilePromises = uniqueUserIds.map(userId =>
        supabase.rpc('get_chat_participant_profile', { _profile_id: userId })
      );

      const profileResults = await Promise.all(profilePromises);
      const validProfiles: Profile[] = [];

      for (const result of profileResults) {
        if (!result.error && result.data && result.data.length > 0) {
          validProfiles.push({
            id: result.data[0].id,
            name: result.data[0].name,
            profile_image: result.data[0].profile_image,
          });
        }
      }

      setProfiles(validProfiles);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast({
        title: t('error'),
        description: t('tryAgain'),
        variant: 'destructive',
      });
    }
  };

  const toggleUser = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const createGroup = async () => {
    if (!user || selectedUsers.length < 2) {
      toast({
        title: t('error'),
        description: t('selectMembers'),
        variant: 'destructive',
      });
      return;
    }

    if (!groupName.trim()) {
      toast({
        title: t('error'),
        description: t('groupName'),
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error(t('sessionExpired'));
      }
      
      const { data: chatId, error: chatError } = await supabase
        .rpc('create_group_chat', {
          _group_name: groupName.trim(),
          _participant_ids: selectedUsers
        });

      if (chatError) {
        console.error('[CREATE GROUP] Chat creation error:', chatError);
        throw chatError;
      }

      toast({
        title: t('success'),
        description: t('success'),
      });

      navigate(`/chat/${chatId}`);
    } catch (error: any) {
      console.error('[CREATE GROUP] Error creating group:', error);
      toast({
        title: t('error'),
        description: error.message || t('tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredProfiles = profiles.filter(profile =>
    profile.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className={`${isMobile ? 'safe-area-screen' : 'flex-1'} flex flex-col bg-background overflow-hidden`}>
      <div className="max-w-2xl mx-auto w-full flex flex-col h-full">
        <div className={`border-b bg-card px-4 py-4 flex items-center gap-3 ${isMobile ? 'pt-[max(env(safe-area-inset-top),1rem)]' : ''} relative flex-shrink-0`}>
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <div className="flex items-center gap-2 flex-1">
            <Users className="h-5 w-5 text-primary" />
            <h1 className="font-semibold text-lg">{t('createGroup')}</h1>
          </div>
          <Button
            onClick={createGroup}
            disabled={loading || selectedUsers.length < 2 || !groupName.trim()}
            className="min-w-[80px]"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t('create')
            )}
          </Button>
        </div>

        <div className="p-4 border-b bg-muted/5 flex-shrink-0">
          <label className="text-sm font-medium mb-2 block">
            {t('groupName')} <span className="text-destructive">*</span>
          </label>
          <Input
            placeholder={t('groupNamePlaceholder')}
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            className="text-lg"
            autoFocus
          />
        </div>

        <div className="p-4 border-b flex-shrink-0">
          <Input
            placeholder={t('search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="px-4 py-2 bg-muted/50 text-sm text-muted-foreground flex-shrink-0">
          {selectedUsers.length} {t('selectMembers')}
        </div>

        <div className="divide-y flex-1 overflow-y-auto pb-[max(env(safe-area-inset-bottom),1rem)]">
          {filteredProfiles.map((profile) => (
            <label
              key={profile.id}
              className="flex items-center gap-3 p-4 hover:bg-muted/50 cursor-pointer"
            >
              <Checkbox
                checked={selectedUsers.includes(profile.id)}
                onCheckedChange={() => toggleUser(profile.id)}
              />
              <Avatar>
                <AvatarImage src={profile.profile_image} />
                <AvatarFallback>{profile.name[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="font-semibold">{profile.name}</h3>
              </div>
            </label>
          ))}

          {filteredProfiles.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              {t('noContactsYet')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateGroupChat;
